import React, { useState, useEffect } from 'react';
import { Box, Typography, Checkbox, Stack, FormControlLabel, FormGroup, Divider, Grid } from '@mui/material';
import SaveButton from '../Components/SaveButton';
import axios from 'axios';
import { APIURL } from '../../../config';

const SecureCommunication = (props) => {
    const [enableMessagingToClients, setEnableMessagingToClients] = useState(false);
    const [enableMessagingToEmployees, setEnableMessagingToEmployees] = useState({
        mtSubmission: props.mtSubmission,
        mtRejected: props.mtRejected,
        mtApproved: props.mtApproved,
        taskAssigned: props.taskAssigned,
        taskStartTimeApproaching: props.taskStartTimeApproaching,
        taskEndTimeApproaching: props.taskEndTimeApproaching,
        missedClockIn: props.missedClockIn,
        missedClockOut: props.missedClockOut,
    });
    const [enableSoSMessaging, setEnableSoSMessaging] = useState(false);
    const [enableMessagingToContacts, setEnableMessagingToContacts] = useState(false);

    // Track saved values
    const [savedMessagingToClients, setSavedMessagingToClients] = useState(false);
    const [savedMessagingToEmployees, setSavedMessagingToEmployees] = useState({
        mtSubmission: props.mtSubmission,
        mtRejected: props.mtRejected,
        mtApproved: props.mtApproved,
        taskAssigned: props.taskAssigned,
        taskStartTimeApproaching: props.taskStartTimeApproaching,
        taskEndTimeApproaching: props.taskEndTimeApproaching,
        missedClockIn: props.missedClockIn,
        missedClockOut: props.missedClockOut,
    });
    const [savedSoSMessaging, setSavedSoSMessaging] = useState(false);
    const [savedMessagingToContacts, setSavedMessagingToContacts] = useState(false);

    useEffect(() => {
        setEnableMessagingToClients(props.enableMessagingToClients);
        setSavedMessagingToClients(props.enableMessagingToClients);
        const employeeSettings = {
            mtSubmission: props.mtSubmission,
            mtRejected: props.mtRejected,
            mtApproved: props.mtApproved,
            taskAssigned: props.taskAssigned,
            taskStartTimeApproaching: props.taskStartTimeApproaching,
            taskEndTimeApproaching: props.taskEndTimeApproaching,
            missedClockIn: props.missedClockIn,
            missedClockOut: props.missedClockOut,
        };
        setEnableMessagingToEmployees(employeeSettings);
        setSavedMessagingToEmployees(employeeSettings);
        setEnableSoSMessaging(props.enableSoSMessaging);
        setSavedSoSMessaging(props.enableSoSMessaging);
        setEnableMessagingToContacts(props.enableMessagingToContacts);
        setSavedMessagingToContacts(props.enableMessagingToContacts);
    }, [props]);

    const saveSetting = async (featureId, value) => {
        try {
            const { data } = await axios.get(`${APIURL}/config/settings`, {
                params: { comp_id: props.comp_id, feature_id: featureId },
            });

            const settingsPayload = {
                feature_value: value ? 'true' : 'false',
                feature_id: featureId,
                company_id: props.comp_id,
                added_by: props.user_id,
            };

            if (data.length > 0) {
                settingsPayload.settings_id = data[0].settings_id;
                await axios.put(`${APIURL}/config/settings`, settingsPayload);
            } else {
                await axios.post(`${APIURL}/config/settings`, settingsPayload);
            }
        } catch (error) {
            console.error(error);
        }
    };

    const handleSave = async () => {
        try {
            // Save employee messaging settings
            if (enableMessagingToEmployees.mtSubmission !== savedMessagingToEmployees.mtSubmission) {
                await saveSetting(60, enableMessagingToEmployees.mtSubmission);
            }
            if (enableMessagingToEmployees.mtRejected !== savedMessagingToEmployees.mtRejected) {
                await saveSetting(61, enableMessagingToEmployees.mtRejected);
            }
            if (enableMessagingToEmployees.mtApproved !== savedMessagingToEmployees.mtApproved) {
                await saveSetting(62, enableMessagingToEmployees.mtApproved);
            }
            if (enableMessagingToEmployees.taskAssigned !== savedMessagingToEmployees.taskAssigned) {
                await saveSetting(63, enableMessagingToEmployees.taskAssigned);
            }
            if (enableMessagingToEmployees.taskStartTimeApproaching !== savedMessagingToEmployees.taskStartTimeApproaching) {
                await saveSetting(64, enableMessagingToEmployees.taskStartTimeApproaching);
            }
            if (enableMessagingToEmployees.taskEndTimeApproaching !== savedMessagingToEmployees.taskEndTimeApproaching) {
                await saveSetting(65, enableMessagingToEmployees.taskEndTimeApproaching);
            }
            if (enableMessagingToEmployees.missedClockIn !== savedMessagingToEmployees.missedClockIn) {
                await saveSetting(83, enableMessagingToEmployees.missedClockIn);
            }
            if (enableMessagingToEmployees.missedClockOut !== savedMessagingToEmployees.missedClockOut) {
                await saveSetting(84, enableMessagingToEmployees.missedClockOut);
            }
            
            // Save other messaging settings
            if (enableMessagingToClients !== savedMessagingToClients) {
                await saveSetting(66, enableMessagingToClients);
            }
            if (enableSoSMessaging !== savedSoSMessaging) {
                await saveSetting(67, enableSoSMessaging);
            }
            if (enableMessagingToContacts !== savedMessagingToContacts) {
                await saveSetting(68, enableMessagingToContacts);
            }

            // Update saved states
            setSavedMessagingToEmployees(enableMessagingToEmployees);
            setSavedMessagingToClients(enableMessagingToClients);
            setSavedSoSMessaging(enableSoSMessaging);
            setSavedMessagingToContacts(enableMessagingToContacts);
        } catch (error) {
            console.error('Save error', error);
        }
    };

    const handleCheckBoxChange = (event, setState) => {
        const { checked } = event.target;
        setState(checked);
    };

    const handleEmployeeMessagingChange = (event, key) => {
        const { checked } = event.target;
        setEnableMessagingToEmployees((prevState) => ({
            ...prevState,
            [key]: checked,
        }));
    };

    return (
        <Box sx={{ flexGrow: 1 }}>
            <Grid container spacing={1}>
                <Grid size={props.leftColumnSize}>
                    <Stack spacing={1}>
                        <Typography variant="h5">
                            Secure Communication
                        </Typography>
                        <Typography variant="body1" sx={{ color: 'gray' }}>
                            Configure your secure communication settings.
                        </Typography>
                    </Stack>
                </Grid>
                <Grid size={props.rightColumnSize}>
                    <Stack spacing={1}>
                        <FormGroup>
                            <Typography variant="h6">Enable messaging to employees</Typography>
                            <Grid container spacing={0}>
                                <Grid size={6}>
                                    <FormControlLabel
                                        sx={{ mb: -1 }}
                                        control={
                                            <Checkbox
                                                checked={enableMessagingToEmployees.mtSubmission}
                                                onChange={(e) => handleEmployeeMessagingChange(e, 'mtSubmission')}
                                                name="mtSubmission"
                                            />
                                        }
                                        label="MT Submission"
                                    />
                                </Grid>
                                <Grid size={6}>
                                    <FormControlLabel
                                        sx={{ mb: -1 }}
                                        control={
                                            <Checkbox
                                                checked={enableMessagingToEmployees.mtRejected}
                                                onChange={(e) => handleEmployeeMessagingChange(e, 'mtRejected')}
                                                name="mtRejected"
                                            />
                                        }
                                        label="MT Rejected"
                                    />
                                </Grid>
                                <Grid size={6}>
                                    <FormControlLabel
                                        sx={{ mb: -1 }}
                                        control={
                                            <Checkbox
                                                checked={enableMessagingToEmployees.mtApproved}
                                                onChange={(e) => handleEmployeeMessagingChange(e, 'mtApproved')}
                                                name="mtApproved"
                                            />
                                        }
                                        label="MT Approved"
                                    />
                                </Grid>
                                <Grid size={6}>
                                    <FormControlLabel
                                        sx={{ mb: -1 }}
                                        control={
                                            <Checkbox
                                                checked={enableMessagingToEmployees.taskAssigned}
                                                onChange={(e) => handleEmployeeMessagingChange(e, 'taskAssigned')}
                                                name="taskAssigned"
                                            />
                                        }
                                        label="Task assigned"
                                    />
                                </Grid>
                                <Grid size={6}>
                                    <FormControlLabel
                                        sx={{ mb: -1 }}
                                        control={
                                            <Checkbox
                                                checked={enableMessagingToEmployees.taskStartTimeApproaching}
                                                onChange={(e) => handleEmployeeMessagingChange(e, 'taskStartTimeApproaching')}
                                                name="taskStartTimeApproaching"
                                            />
                                        }
                                        label="Task start time approaching"
                                    />
                                </Grid>
                                <Grid size={6}>
                                    <FormControlLabel
                                        sx={{ mb: -1 }}
                                        control={
                                            <Checkbox
                                                checked={enableMessagingToEmployees.taskEndTimeApproaching}
                                                onChange={(e) => handleEmployeeMessagingChange(e, 'taskEndTimeApproaching')}
                                                name="taskEndTimeApproaching"
                                            />
                                        }
                                        label="Task end time approaching"
                                    />
                                </Grid>
                                {props.isHomeHealthcare && (
                                    <>
                                        <Grid size={6}>
                                            <FormControlLabel
                                                sx={{ mb: -1 }}
                                                control={
                                                    <Checkbox
                                                        checked={enableMessagingToEmployees.missedClockIn}
                                                        onChange={(e) => handleEmployeeMessagingChange(e, 'missedClockIn')}
                                                        name="missedClockIn"
                                                    />
                                                }
                                                label="Missed Clock-In"
                                            />
                                        </Grid>
                                        <Grid size={6}>
                                            <FormControlLabel
                                                sx={{ mb: -1 }}
                                                control={
                                                    <Checkbox
                                                        checked={enableMessagingToEmployees.missedClockOut}
                                                        onChange={(e) => handleEmployeeMessagingChange(e, 'missedClockOut')}
                                                        name="missedClockOut"
                                                    />
                                                }
                                                label="Missed Clock-Out"
                                            />
                                        </Grid>
                                    </>
                                )}
                            </Grid>
                            <Divider sx={{ my: 1 }} />
                            <FormControlLabel
                                sx={{ mb: -1 }}
                                control={
                                    <Checkbox
                                        checked={enableMessagingToClients}
                                        onChange={(e) => handleCheckBoxChange(e, setEnableMessagingToClients)}
                                        name="enableMessagingToClients"
                                    />
                                }
                                label="Enable messaging to clients"
                            />
                            <Divider sx={{ my: 1 }} />
                            <FormControlLabel
                                sx={{ mb: -1 }}
                                control={
                                    <Checkbox
                                        checked={enableSoSMessaging}
                                        onChange={(e) => handleCheckBoxChange(e, setEnableSoSMessaging)}
                                        name="enableSoSMessaging"
                                    />
                                }
                                label="Enable SoS messaging"
                            />
                            <FormControlLabel
                                sx={{ mb: -1 }}
                                control={
                                    <Checkbox
                                        checked={enableMessagingToContacts}
                                        onChange={(e) => handleCheckBoxChange(e, setEnableMessagingToContacts)}
                                        name="enableMessagingToContacts"
                                    />
                                }
                                label="Enable messaging to Contacts"
                            />
                        </FormGroup>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 2 }}>
                            <SaveButton
                                onClick={handleSave}
                                disabled={
                                    enableMessagingToClients === savedMessagingToClients &&
                                    enableSoSMessaging === savedSoSMessaging &&
                                    enableMessagingToContacts === savedMessagingToContacts &&
                                    enableMessagingToEmployees.mtSubmission === savedMessagingToEmployees.mtSubmission &&
                                    enableMessagingToEmployees.mtRejected === savedMessagingToEmployees.mtRejected &&
                                    enableMessagingToEmployees.mtApproved === savedMessagingToEmployees.mtApproved &&
                                    enableMessagingToEmployees.taskAssigned === savedMessagingToEmployees.taskAssigned &&
                                    enableMessagingToEmployees.taskStartTimeApproaching === savedMessagingToEmployees.taskStartTimeApproaching &&
                                    enableMessagingToEmployees.taskEndTimeApproaching === savedMessagingToEmployees.taskEndTimeApproaching &&
                                    enableMessagingToEmployees.missedClockIn === savedMessagingToEmployees.missedClockIn &&
                                    enableMessagingToEmployees.missedClockOut === savedMessagingToEmployees.missedClockOut
                                }
                            />
                        </Box>
                    </Stack>
                </Grid>
            </Grid>
        </Box>
    );
};

export default SecureCommunication;
